import React, { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { Colors } from '../../styles/colors';
import {

    TextField,
    InputAdornment,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
    Checkbox,
    ListItemText,
    SelectChangeEvent,
    CircularProgress,
    Alert,
    Box
} from '@mui/material';
import {
    Search as SearchIcon,
    Add as AddIcon,
    Close as CloseIcon
} from '@mui/icons-material';

import Layout from '../../components/Layout';
import CustomStyledDataGrid from '../../components/StyledDataGrid';
import DatePicker from '../../components/DatePicker';
import {
    riskLevels,
    filterVerifications,
    createColumns,
} from './logic';
import {
    StyledContainer,
    StyledTitle,
    StyledFiltersContainer,
    StyledSearchField,
    StyledDateField,
    StyledRiskSelect,
    StyledClearButton,
    StyledAddButton,
    StyledViewDetailsButton,
    StyledBusinessCell,
    StyledRiskCell
} from './styles';
import CreateForm from './CreateForm';
import { VerificationQueries } from '../../data/verification/verification.queries';

const BusinessesView: React.FC = () => {
    const navigate = useNavigate();
    const { tenant } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();

    // Filter/pagination state lives in URL query params so it survives navigation.
    // Array filters (risks, tenants) use repeated params so that values containing
    // commas (e.g. tenant names like "Acme, Inc.") round-trip correctly.
    const searchTerm = searchParams.get('search') || '';
    const dateFilter = searchParams.get('date') || '';
    const selectedRisks = useMemo(
        () => searchParams.getAll('risks'),
        [searchParams]
    );
    const selectedTenantNames = useMemo(
        () => searchParams.getAll('tenants'),
        [searchParams]
    );
    const page = parseInt(searchParams.get('page') || '1', 10);

    const updateParams = (updates: Record<string, string | string[] | number | null>) => {
        const newParams = new URLSearchParams(searchParams);
        Object.entries(updates).forEach(([key, value]) => {
            if (
                value === null ||
                value === '' ||
                (Array.isArray(value) && value.length === 0)
            ) {
                newParams.delete(key);
            } else if (Array.isArray(value)) {
                newParams.delete(key);
                value.forEach((v) => newParams.append(key, v));
            } else {
                newParams.set(key, String(value));
            }
        });
        setSearchParams(newParams, { replace: true });
    };

    const [createModalOpen, setCreateModalOpen] = useState(false);

    // Fetch verifications data using the custom hook
    const { data, isLoading, error } = VerificationQueries.useGetVerifications(1);

    const verifications = useMemo(
        () => data?.verifications ?? [],
        [data?.verifications]
    );
    // changed: This remains the total count in the backend
    const totalVerifications = data?.total || 0;

    // Gather unique tenant names for the filter dropdown
    const tenantNames = useMemo(() =>
        Array.from(new Set(verifications.map((v) => v.tenant_name || '').filter(Boolean)))
            .sort((a, b) => a.localeCompare(b))
        , [verifications]);

    // Check if current tenant is the admin tenant
    const isAdmin = tenant?.is_admin_tenant ?? false;

    // Filter verifications based on search, date, risk filters, and tenant name
    const filteredVerifications = useMemo(() => {
        let filtered = filterVerifications(
            verifications,
            searchTerm,
            dateFilter,
            selectedRisks,
            isAdmin
        );
        if (selectedTenantNames.length > 0) {
            filtered = filtered.filter(v => selectedTenantNames.includes(v.tenant_name));
        }
        return filtered;
    }, [verifications, searchTerm, dateFilter, selectedRisks, selectedTenantNames, isAdmin]);

    // This is the count that should be shown and updated w/ filters
    const filteredCount = filteredVerifications.length;

    // Ensure current page doesn't exceed available pages after filtering
    const pageSize = 20;
    const maxPage = Math.max(1, Math.ceil(filteredCount / pageSize));
    const safePage = Math.min(page, maxPage);

    // Handle risk filter change
    const handleRiskChange = (event: SelectChangeEvent<typeof selectedRisks>) => {
        const value = event.target.value;
        const risks = typeof value === 'string' ? value.split(',') : value;
        updateParams({ risks, page: null });
    };

    // Handle tenant name filter change
    const handleTenantChange = (event: SelectChangeEvent<typeof selectedTenantNames>) => {
        const value = event.target.value;
        const tenants = typeof value === 'string' ? value.split(',') : value;
        updateParams({ tenants, page: null });
    };

    // Handle add business button click
    const handleAddBusiness = () => {
        setCreateModalOpen(true);
    };

    const activeFilterCount =
        (searchTerm ? 1 : 0) +
        (dateFilter ? 1 : 0) +
        (selectedRisks.length > 0 ? 1 : 0) +
        (selectedTenantNames.length > 0 ? 1 : 0);

    const clearFilters = () => {
        setSearchParams(new URLSearchParams(), { replace: true });
    };

    // Memoize columns configuration to prevent unnecessary re-renders
    const columns = useMemo(() => createColumns(
        navigate,
        StyledBusinessCell,
        StyledRiskCell,
        StyledViewDetailsButton,
        isAdmin
    ), [navigate, isAdmin]);

    if (isLoading) {
        return (
            <Layout title="Businesses">
                <Box display="flex" justifyContent="center" mt={4}>
                    <CircularProgress />
                </Box>
            </Layout>
        );
    }

    if (error) {
        return (
            <Layout title="Businesses">
                <Alert severity="error" sx={{ mt: 2 }}>
                    {(error as Error).message}
                </Alert>
            </Layout>
        );
    }

    return (
        <Layout title="Businesses">
            <StyledContainer>
                {/* Header with count */}
                <StyledTitle>
                    All businesses ({filteredCount})
                </StyledTitle>

                {/* Filters row */}
                <StyledFiltersContainer>
                    {/* Search field */}
                    <StyledSearchField>
                        <TextField
                            placeholder="Search..."
                            sx={{
                                '& .MuiInputBase-input::placeholder': {
                                    color: Colors.LIGHT_GRAY,
                                    opacity: 1,
                                },
                            }}
                            variant="outlined"
                            size="small"
                            value={searchTerm}
                            onChange={(e) => {
                                updateParams({ search: e.target.value, page: null });
                            }}
                            fullWidth
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: Colors.LIGHT_GRAY, fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </StyledSearchField>

                    {/* Date range picker */}
                    <StyledDateField>
                        <DatePicker
                            value={dateFilter}
                            onChange={(value) => {
                                updateParams({ date: value, page: null });
                            }}
                            placeholder="MM/DD/YYYY - MM/DD/YYYY"
                        />
                    </StyledDateField>

                    {/* Tenant name multi-select - only show if there is more than 1 tenant */}
                    {tenantNames.length > 1 && (
                        <StyledRiskSelect>
                            <FormControl size="small" fullWidth>
                                <InputLabel sx={{ fontSize: '14px', color: Colors.NEUTRAL_GRAY }}>Select tenant</InputLabel>
                                <Select
                                    multiple
                                    value={selectedTenantNames}
                                    onChange={handleTenantChange}
                                    input={<OutlinedInput label="Select tenant" />}
                                    sx={{
                                        color: Colors.NEUTRAL_GRAY,
                                        fontSize: '14px'
                                    }}
                                    renderValue={(selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.map((value) => (
                                                <Chip key={value} label={value} size="small" />
                                            ))}
                                        </Box>
                                    )}
                                >
                                    {tenantNames.map((tenant) => (
                                        <MenuItem
                                            key={tenant}
                                            value={tenant}
                                            sx={{
                                                fontSize: '14px',
                                                fontWeight: 500,
                                                color: '#4A5075',
                                                '&:hover': {
                                                    backgroundColor: '#F5F6FA',
                                                    color: '#2C3E50',
                                                },
                                                '&.Mui-selected': {
                                                    backgroundColor: '#F7F9FF',
                                                    color: '#2C3E50',
                                                    '&:hover': {
                                                        backgroundColor: '#E8F0FE',
                                                    },
                                                },
                                            }}
                                        >
                                            <Checkbox
                                                checked={selectedTenantNames.indexOf(tenant) > -1}
                                                sx={{
                                                    color: '#C4C9DA',
                                                    '&.Mui-checked': {
                                                        color: Colors.BRAND_BLUE,
                                                    },
                                                }}
                                            />
                                            <ListItemText
                                                primary={tenant}
                                                sx={{
                                                    '& .MuiListItemText-primary': {
                                                        fontSize: '14px',
                                                        fontWeight: 400,
                                                        color: Colors.NEUTRAL_GRAY,
                                                    },
                                                }}
                                            />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </StyledRiskSelect>
                    )}

                    {/* Risk level multi-select */}
                    <StyledRiskSelect>
                        <FormControl size="small" fullWidth>
                            <InputLabel sx={{ fontSize: '14px', color: Colors.NEUTRAL_GRAY }}>Select risks</InputLabel>
                            <Select
                                multiple
                                value={selectedRisks}
                                onChange={handleRiskChange}
                                input={<OutlinedInput label="Select risks" />}
                                sx={{
                                    color: Colors.NEUTRAL_GRAY,
                                    fontSize: '14px'
                                }}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((value) => (
                                            <Chip key={value} label={value} size="small" />
                                        ))}
                                    </Box>
                                )}
                            >
                                {riskLevels.map((risk) => (
                                    <MenuItem
                                        key={risk}
                                        value={risk}
                                        sx={{
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            color: '#4A5075', // softer gray like screenshot
                                            '&:hover': {
                                                backgroundColor: '#F5F6FA',
                                                color: '#2C3E50',
                                            },
                                            '&.Mui-selected': {
                                                backgroundColor: '#F7F9FF', // very light blue
                                                color: '#2C3E50',
                                                '&:hover': {
                                                    backgroundColor: '#E8F0FE', // slightly darker hover blue
                                                },
                                            },
                                        }}
                                    >
                                        <Checkbox
                                            checked={selectedRisks.indexOf(risk) > -1}
                                            sx={{
                                                color: '#C4C9DA', // unchecked muted gray
                                                '&.Mui-checked': {
                                                    color: Colors.BRAND_BLUE, // vivid blue check
                                                },
                                            }}
                                        />
                                        <ListItemText
                                            primary={risk}
                                            sx={{
                                                '& .MuiListItemText-primary': {
                                                    fontSize: '14px',
                                                    fontWeight: 400,
                                                    color: Colors.NEUTRAL_GRAY,
                                                },
                                            }}
                                        />
                                    </MenuItem>
                                ))}

                            </Select>
                        </FormControl>
                    </StyledRiskSelect>

                    {/* Clear filters (slot reserved when inactive to avoid layout shift) */}
                    <StyledClearButton
                        onClick={clearFilters}
                        startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
                        sx={{
                            visibility: activeFilterCount ? 'visible' : 'hidden',
                            mr: 0.5,
                        }}
                    >
                        {activeFilterCount > 1
                            ? `Clear filters (${activeFilterCount})`
                            : 'Clear filters'}
                    </StyledClearButton>

                    {/* Add business button */}
                    <StyledAddButton
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddBusiness}
                    >
                        Add a business
                    </StyledAddButton>
                </StyledFiltersContainer>

                {/* Data Grid */}
                <CustomStyledDataGrid
                    rows={filteredVerifications}
                    columns={columns}
                    pageSize={pageSize}
                    loading={isLoading}
                    getRowId={(row) => row.id}
                    page={safePage}
                    onPageChange={(newPage) => updateParams({ page: newPage === 1 ? null : newPage })}
                    isAdmin={isAdmin}
                />

                {/* Create Business Modal */}
                <CreateForm
                    open={createModalOpen}
                    onClose={() => setCreateModalOpen(false)}
                />
            </StyledContainer>
        </Layout>
    );
};

export default BusinessesView;
